import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ICampoCustomizableCatalogo } from '../interfaces/campo-customizable-catalogo.interface';
import { CamposCustomizablesCatalogosService } from '../services/campos-customizables-catalogos.service';
import { SessionService } from '@shared/helpers/sesion.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-campo-customizable-catalogo-form',
  templateUrl: './campo-customizable-catalogo-form.component.html',
  providers: [MessageService]
})
export class CampoCustomizableCatalogoFormComponent implements OnInit, OnChanges {
  @Input() model: ICampoCustomizableCatalogo | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;

  // Opciones para los dropdowns
  tiposOptions: { label: string; value: string }[] = [];
  tablasOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private camposCustomizablesCatalogosService: CamposCustomizablesCatalogosService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadTiposOptions();
    this.loadTablasOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      this.initForm();
      if (changes['model'].currentValue) {
        this.loadCampoCustomizableCatalogo();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      sCampo: [{ value: '', disabled: !!this.model }, [Validators.required]],
      sTipo: ['', [Validators.required]],
      sTabla: [{ value: '', disabled: !!this.model }, [Validators.required]],
      iLongitud: [0, [Validators.required, Validators.min(0)]]
    });
  }

  loadTiposOptions(): void {
    this.camposCustomizablesCatalogosService.getTiposOptions().subscribe({
      next: (response) => {
        if (response.success) {
          this.tiposOptions = response.data.map((opcion) => ({
            label: opcion.sOpcion,
            value: opcion.sValor
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tipo'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tipo'
        });
      }
    });
  }

  loadTablasOptions(): void {
    const sUsuario = this.sessionService.getUserSession();
    this.camposCustomizablesCatalogosService.getTablasOptions(sUsuario).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.tablasOptions = response.data.map((opcion) => ({
            label: opcion.sEtiqueta,
            value: opcion.sTabla
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tabla'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tabla'
        });
      }
    });
  }

  loadCampoCustomizableCatalogo(): void {
    if (!this.model) return;
    
    this.form.patchValue({
      sCampo: this.model.sCampo,
      sTipo: this.model.sTipo,
      sTabla: this.model.sTabla,
      iLongitud: this.model.iLongitud
    });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formValue = this.form.getRawValue();
    
    const campo: ICampoCustomizableCatalogo = {
      sCampo: formValue.sCampo,
      sTabla: formValue.sTabla,
      sTipo: formValue.sTipo,
      iLongitud: formValue.iLongitud
    };

    this.camposCustomizablesCatalogosService.saveCampoCustomizableCatalogo(campo).subscribe({
      next: (response) => {
        this.loading = false;
        const errorCode = response.data?.[0]?.error;
        if (errorCode === "0") {
          this.onSave.emit({
            success: true,
            message: 'Campo customizable catálogo creado correctamente'
          });
        } else {
          this.onSave.emit({
            success: false,
            message: errorCode || 'Error al crear el campo customizable catálogo'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.loading = false;
        this.onSave.emit({
          success: false,
          message: 'Error al crear el campo customizable catálogo'
        });
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formValue = this.form.getRawValue();
    
    const campo: ICampoCustomizableCatalogo = {
      sCampo: formValue.sCampo,
      sTabla: formValue.sTabla,
      sTipo: formValue.sTipo,
      iLongitud: formValue.iLongitud,
      sCampoValida: '',
      sOperador: ''
    };

    this.camposCustomizablesCatalogosService.updateCampoCustomizableCatalogo(campo).subscribe({
      next: (response) => {
        this.loading = false;
        const errorCode = response.data?.[0]?.error;
        if (errorCode === "0") {
          this.onSave.emit({
            success: true,
            message: 'Campo customizable catálogo actualizado correctamente'
          });
        } else {
          this.onSave.emit({
            success: false,
            message: errorCode || 'Error al actualizar el campo customizable catálogo'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.loading = false;
        this.onSave.emit({
          success: false,
          message: 'Error al actualizar el campo customizable catálogo'
        });
      }
    });
  }
} 